/**
 * Year-over-Year Tab — Redesigned
 *
 * Improvements:
 *  3.1 — Quantity/Value toggle
 *  3.2 — Side-by-side monthly comparison
 *  3.3 — Color-differentiate Actual (dark) vs Forecast (light) for current year
 *  3.4 — Total column/row
 *  3.5 — Budget bar (when data available)
 *  3.6 — Data labels on bars
 *  3.7 — Fix filters (pass brand/category/city)
 *  3.8 — Product filter with UnifiedProductSearch
 */
import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Grid, TextField, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LabelList,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import ExportMenu from '../Common/ExportMenu';
import { useForecastingFilters } from './ForecastingFilterContext';
import UnifiedProductSearch, { ProductOption } from '../Common/UnifiedProductSearch';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { useFullscreen, FullscreenButton } from '../Common/FullscreenButton';
import { CHART } from '../../constants/chartColors';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */


const fmtLabel = (value: number): string => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return String(value);
};

const fmtNumber = (v: number | undefined): string =>
  v != null ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—';

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const YearOverYearTab: React.FC = () => {
  const { brands, categories, stores: selectedStoreIds, cities, dateRange } = useForecastingFilters();
  const { fmtCurrency } = useCurrencyFormat();

  const [yoyYear1, setYoyYear1] = useState(new Date().getFullYear() - 1);
  const [yoyYear2, setYoyYear2] = useState(new Date().getFullYear());
  const [selectedProduct, setSelectedProduct] = useState<ProductOption[]>([]);
  const [viewMode, setViewMode] = useState<'quantity' | 'value'>('quantity');

  const { isFullscreen, toggle: toggleFullscreen, FullscreenWrap } = useFullscreen();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [yoyData, setYoyData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* Column filters */
  const [filterMonth, setFilterMonth] = useState('');
  const [filterQtyChange, setFilterQtyChange] = useState('');
  const [filterRevChange, setFilterRevChange] = useState('');

  const loadYoYComparison = async () => {
    setLoading(true);
    setError(null);
    try {
      const locationId = selectedStoreIds.length === 1 ? selectedStoreIds[0] : undefined;
      const productId = selectedProduct[0]?.product_id;
      const fmtDate = (d: Date | null) => d ? d.toISOString().slice(0, 10) : undefined;
      const response = await api.getYearOverYearComparison(
        yoyYear1,
        yoyYear2,
        productId || undefined,
        locationId,
        {
          brand: brands.length > 0 ? brands.join(',') : undefined,
          category: categories.length > 0 ? categories.join(',') : undefined,
          city: cities.length > 0 ? cities.join(',') : undefined,
          stores: selectedStoreIds.length > 1 ? selectedStoreIds : undefined,
          dateFrom: fmtDate(dateRange.start),
          dateTo: fmtDate(dateRange.end),
        },
      );
      setYoyData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load YoY comparison');
    } finally {
      setLoading(false);
    }
  };

  /* Apply column filters */
  const filteredData = useMemo(() => {
    if (!yoyData?.monthly_comparison) return [];
    return yoyData.monthly_comparison.filter((month: any) => {
      if (filterMonth && !month.month_name.toLowerCase().includes(filterMonth.toLowerCase())) return false;
      if (filterQtyChange) {
        const threshold = parseFloat(filterQtyChange);
        if (!isNaN(threshold) && Math.abs(month.quantity_change_pct) < threshold) return false;
      }
      if (filterRevChange) {
        const threshold = parseFloat(filterRevChange);
        if (!isNaN(threshold) && Math.abs(month.revenue_change_pct) < threshold) return false;
      }
      return true;
    });
  }, [yoyData, filterMonth, filterQtyChange, filterRevChange]);

  /* Totals row */
  const totals = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return null;
    const t = {
      year1_quantity: 0, year2_quantity: 0,
      year1_revenue: 0, year2_revenue: 0,
      budget: 0,
    };
    filteredData.forEach((m: any) => {
      t.year1_quantity += m.year1_quantity || 0;
      t.year2_quantity += m.year2_quantity || 0;
      t.year1_revenue += m.year1_revenue || 0;
      t.year2_revenue += m.year2_revenue || 0;
      t.budget += m.budget || 0;
    });
    return {
      ...t,
      quantity_change_pct: t.year1_quantity > 0 ? ((t.year2_quantity - t.year1_quantity) / t.year1_quantity * 100) : 0,
      revenue_change_pct: t.year1_revenue > 0 ? ((t.year2_revenue - t.year1_revenue) / t.year1_revenue * 100) : 0,
    };
  }, [filteredData]);

  const isCurrentYear = yoyYear2 === new Date().getFullYear();

  /* Chart data with actual/forecast differentiation for year2 */
  const chartData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    return filteredData.map((m: any) => {
      const isForecast = m.is_forecast === true;
      const isActual = isCurrentYear && !isForecast;

      if (viewMode === 'quantity') {
        return {
          month_name: m.month_name,
          year1: m.year1_quantity || 0,
          year2_actual: isActual ? (m.year2_quantity || 0) : 0,
          year2_forecast: isForecast ? (m.year2_quantity || 0) : 0,
          year2_full: !isCurrentYear ? (m.year2_quantity || 0) : 0, // when year2 isn't current year
          budget: m.budget_quantity || 0,
        };
      }
      return {
        month_name: m.month_name,
        year1: m.year1_revenue || 0,
        year2_actual: isActual ? (m.year2_revenue || 0) : 0,
        year2_forecast: isForecast ? (m.year2_revenue || 0) : 0,
        year2_full: !isCurrentYear ? (m.year2_revenue || 0) : 0,
        budget: m.budget_revenue || m.budget || 0,
      };
    });
  }, [filteredData, viewMode, isCurrentYear]);

  /* Export rows for <ExportMenu> */
  const exportRows = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    const rows: Record<string, any>[] = filteredData.map((m: any) => ({
      Month: m.month_name,
      [`${yoyYear1} Qty`]: m.year1_quantity?.toFixed(0),
      [`${yoyYear2} Qty`]: m.year2_quantity?.toFixed(0),
      'Qty Change %': `${m.quantity_change_pct?.toFixed(1)}%`,
      [`${yoyYear1} Revenue`]: m.year1_revenue?.toFixed(2),
      [`${yoyYear2} Revenue`]: m.year2_revenue?.toFixed(2),
      'Revenue Change %': `${m.revenue_change_pct?.toFixed(1)}%`,
    }));
    if (totals) {
      rows.push({
        Month: 'TOTAL',
        [`${yoyYear1} Qty`]: totals.year1_quantity.toFixed(0),
        [`${yoyYear2} Qty`]: totals.year2_quantity.toFixed(0),
        'Qty Change %': `${totals.quantity_change_pct.toFixed(1)}%`,
        [`${yoyYear1} Revenue`]: totals.year1_revenue.toFixed(2),
        [`${yoyYear2} Revenue`]: totals.year2_revenue.toFixed(2),
        'Revenue Change %': `${totals.revenue_change_pct.toFixed(1)}%`,
      });
    }
    return rows;
  }, [filteredData, totals, yoyYear1, yoyYear2]);

  const handleProductChange = useCallback((products: ProductOption[]) => {
    setSelectedProduct(products);
  }, []);

  return (
    <FullscreenWrap>
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
          Year-over-Year Comparison
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {yoyData && (
            <ExportMenu
              data={exportRows}
              filename={`YoY_Comparison_${yoyYear1}_vs_${yoyYear2}`}
              title={`Year-over-Year Comparison ${yoyYear1} vs ${yoyYear2}`}
              sheetName="Year-over-Year"
              chartRef={chartContainerRef}
            />
          )}
          <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
        <Grid item xs={6} md={1.5}>
          <TextField fullWidth size="small" type="number" label="Year 1" value={yoyYear1} onChange={(e) => setYoyYear1(parseInt(e.target.value))} />
        </Grid>
        <Grid item xs={6} md={1.5}>
          <TextField fullWidth size="small" type="number" label="Year 2" value={yoyYear2} onChange={(e) => setYoyYear2(parseInt(e.target.value))} />
        </Grid>
        <Grid item xs={12} md={4}>
          <UnifiedProductSearch
            value={selectedProduct}
            onChange={handleProductChange}
            label="Product (optional)"
            placeholder="All products"
            singleSelect
            brandFilter={brands}
            categoryFilter={categories}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            variant="contained" size="small"
            startIcon={<Refresh />}
            onClick={loadYoYComparison}
            fullWidth sx={{ height: '40px' }}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Compare'}
          </Button>
        </Grid>
      </Grid>

      {yoyData?.summary && (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2.4}>
              <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>{fmtNumber(yoyData.summary.year1_total_quantity)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px' }}>{yoyYear1} Qty</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>{fmtNumber(yoyData.summary.year2_total_quantity)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px' }}>{yoyYear2} Qty</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: yoyData.summary.total_quantity_change_pct > 0 ? 'rgba(46,125,50,0.08)' : 'rgba(198,40,40,0.08)' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: yoyData.summary.total_quantity_change_pct > 0 ? '#2e7d32' : '#c62828' }}>
                  {yoyData.summary.total_quantity_change_pct > 0 ? '+' : ''}{yoyData.summary.total_quantity_change_pct?.toFixed(1)}%
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '11px' }}>Qty Change</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>{fmtCurrency(yoyData.summary.year2_total_revenue)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px' }}>{yoyYear2} Revenue</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: yoyData.summary.total_revenue_change_pct > 0 ? 'rgba(46,125,50,0.08)' : 'rgba(198,40,40,0.08)' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: yoyData.summary.total_revenue_change_pct > 0 ? '#2e7d32' : '#c62828' }}>
                  {yoyData.summary.total_revenue_change_pct > 0 ? '+' : ''}{yoyData.summary.total_revenue_change_pct?.toFixed(1)}%
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '11px' }}>Rev Change</Typography>
              </Paper>
            </Grid>
          </Grid>

          {yoyData.monthly_comparison && (
            <>
              {/* Quantity/Value toggle */}
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                  Monthly {viewMode === 'quantity' ? 'Quantity' : 'Revenue'} Comparison
                </Typography>
                <ToggleButtonGroup
                  value={viewMode} exclusive
                  onChange={(_, v) => v && setViewMode(v)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': { fontSize: '11px', px: 1.5, py: 0.3, textTransform: 'none' },
                    '& .Mui-selected': { bgcolor: 'var(--color-accent-bg) !important', color: 'var(--color-accent) !important', fontWeight: 600 },
                  }}
                >
                  <ToggleButton value="quantity">Quantity</ToggleButton>
                  <ToggleButton value="value">Revenue</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Chart */}
              <Paper ref={chartContainerRef} sx={{ p: 2, mb: 2 }}>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={chartData} barGap={0} barCategoryGap="15%">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="month_name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtLabel} />
                    <Tooltip formatter={(value: number) => viewMode === 'value' ? fmtCurrency(value) : fmtNumber(value)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="year1" fill={CHART.taupe} name={`${yoyYear1}`} radius={[2, 2, 0, 0]}>
                      <LabelList dataKey="year1" position="top" fontSize={9} formatter={fmtLabel} />
                    </Bar>
                    {isCurrentYear ? (
                      <>
                        <Bar dataKey="year2_actual" fill={CHART.secondary} name={`${yoyYear2} (Actual)`} stackId="year2" radius={[2, 2, 0, 0]}>
                          <LabelList dataKey="year2_actual" position="top" fontSize={9} formatter={(v: number) => v > 0 ? fmtLabel(v) : ''} />
                        </Bar>
                        <Bar dataKey="year2_forecast" fill={CHART.rose} name={`${yoyYear2} (Forecast)`} stackId="year2" radius={[2, 2, 0, 0]}>
                          <LabelList dataKey="year2_forecast" position="top" fontSize={9} formatter={(v: number) => v > 0 ? fmtLabel(v) : ''} />
                        </Bar>
                      </>
                    ) : (
                      <Bar dataKey="year2_full" fill={CHART.secondary} name={`${yoyYear2}`} radius={[2, 2, 0, 0]}>
                        <LabelList dataKey="year2_full" position="top" fontSize={9} formatter={fmtLabel} />
                      </Bar>
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </Paper>

              {/* Table with column-level filters */}
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    {/* Filter row */}
                    <TableRow sx={{ bgcolor: 'var(--color-bg-secondary)' }}>
                      <TableCell>
                        <TextField size="small" placeholder="Filter month..." value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} variant="standard" fullWidth inputProps={{ style: { fontSize: '11px' } }} />
                      </TableCell>
                      <TableCell /><TableCell />
                      <TableCell>
                        <TextField size="small" placeholder="Min % ..." value={filterQtyChange} onChange={(e) => setFilterQtyChange(e.target.value)} variant="standard" type="number" fullWidth inputProps={{ style: { fontSize: '11px' } }} />
                      </TableCell>
                      <TableCell /><TableCell />
                      <TableCell>
                        <TextField size="small" placeholder="Min % ..." value={filterRevChange} onChange={(e) => setFilterRevChange(e.target.value)} variant="standard" type="number" fullWidth inputProps={{ style: { fontSize: '11px' } }} />
                      </TableCell>
                    </TableRow>
                    {/* Header row */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Month</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>{yoyYear1} Qty</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>{yoyYear2} Qty</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Qty Change</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>{yoyYear1} Rev</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>{yoyYear2} Rev</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Rev Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.map((month: any) => {
                      const isActual = isCurrentYear && !month.is_forecast;
                      return (
                        <TableRow key={month.month} hover>
                          <TableCell sx={{ fontSize: '12px' }}>
                            {month.month_name}
                            {isCurrentYear && (
                              <Chip
                                label={isActual ? 'Actual' : 'Forecast'}
                                size="small" variant="outlined"
                                color={isActual ? 'success' : 'default'}
                                sx={{ ml: 1, fontSize: '9px', height: 18 }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>{fmtNumber(month.year1_quantity)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '12px', fontVariantNumeric: 'tabular-nums', fontWeight: 500, color: isActual ? '#1565c0' : 'var(--color-text-primary)' }}>
                            {fmtNumber(month.year2_quantity)}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${month.quantity_change_pct > 0 ? '+' : ''}${month.quantity_change_pct?.toFixed(1)}%`}
                              color={month.quantity_change_pct > 0 ? 'success' : 'error'}
                              size="small" variant="outlined"
                              sx={{ fontSize: '11px', height: 22 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(month.year1_revenue)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '12px', fontVariantNumeric: 'tabular-nums', fontWeight: 500, color: isActual ? '#1565c0' : 'var(--color-text-primary)' }}>
                            {fmtCurrency(month.year2_revenue)}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${month.revenue_change_pct > 0 ? '+' : ''}${month.revenue_change_pct?.toFixed(1)}%`}
                              color={month.revenue_change_pct > 0 ? 'success' : 'error'}
                              size="small" variant="outlined"
                              sx={{ fontSize: '11px', height: 22 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Total row */}
                    {totals && (
                      <TableRow sx={{ bgcolor: 'var(--color-bg-secondary)' }}>
                        <TableCell sx={{ fontSize: '12px', fontWeight: 700 }}>TOTAL</TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtNumber(totals.year1_quantity)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtNumber(totals.year2_quantity)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${totals.quantity_change_pct > 0 ? '+' : ''}${totals.quantity_change_pct.toFixed(1)}%`}
                            color={totals.quantity_change_pct > 0 ? 'success' : 'error'}
                            size="small"
                            sx={{ fontSize: '11px', height: 22, fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(totals.year1_revenue)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(totals.year2_revenue)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${totals.revenue_change_pct > 0 ? '+' : ''}${totals.revenue_change_pct.toFixed(1)}%`}
                            color={totals.revenue_change_pct > 0 ? 'success' : 'error'}
                            size="small"
                            sx={{ fontSize: '11px', height: 22, fontWeight: 700 }}
                          />
                        </TableCell>
                      </TableRow>
                    )}

                    {filteredData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary" sx={{ fontSize: '13px' }}>No data matches the filters</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      {!yoyData && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
            Select years and click "Compare" to view year-over-year data. Use global filters above to narrow by brand, category, or store.
          </Typography>
        </Paper>
      )}
    </Box>
    </FullscreenWrap>
  );
};

export default YearOverYearTab;
